import { Head, Link } from "@inertiajs/react"
import { AdminLayout } from "@/components/layouts/admin-layout"
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { MainContainer } from "@/components/ui/container"
import { PageHeader } from "@/components/ui/headers"
import { attemptStatusVariant } from "@/features/admin/transactions"
import type { TransactionDetail } from "@/features/admin/transactions"
import { intlLocale, t } from "@/lib/i18n"

type Props = { transaction: TransactionDetail }

/** Platform time. This screen crosses tenants; see the index for why. */
function formatDateTime(value: string) {
  return new Intl.DateTimeFormat(intlLocale(), { dateStyle: "medium", timeStyle: "short" }).format(
    new Date(value)
  )
}

function money(cents: number, currency: string) {
  return new Intl.NumberFormat(intlLocale(), {
    style: "currency",
    currency: currency.toUpperCase(),
  }).format(cents / 100)
}

/**
 * One fact per row. Figures stay `text-foreground` and tabular — colour lives in
 * the badges, per the design system's dot rule.
 */
function Fact({ label, value, mono = false }: { label: string; value: React.ReactNode; mono?: boolean }) {
  return (
    <div className="flex flex-col gap-0.5 border-b border-border py-2 last:border-b-0 sm:flex-row sm:items-baseline sm:justify-between sm:gap-4">
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd
        className={`text-sm text-foreground tabular-nums ${mono ? "font-mono break-all text-xs" : ""}`}
      >
        {value ?? "—"}
      </dd>
    </div>
  )
}

export default function AdminTransactionShow({ transaction }: Props) {
  const { provider } = transaction
  const key = (name: string) => `ui.react.pages.admin.transactions.show.${name}`

  return (
    <AdminLayout>
      <Head title={t(key("title"), { id: transaction.id })} />

      <MainContainer size="md">
        <PageHeader
          title={t(key("title"), { id: transaction.id })}
          description={`${transaction.location.name} · ${formatDateTime(transaction.createdAt)}`}
          actions={
            <Button asChild variant="outline" size="sm">
              <Link href="/transactions" data-testid="transaction-back">
                {t(key("back"))}
              </Link>
            </Button>
          }
        />

        {/* Above everything, because it is the answer when there is one. The
            provider's message is the specific one; `failureReason` is what the
            attempt recorded, and they agree on a Stripe refusal but not on a
            manual resolution. */}
        {transaction.failureReason || provider?.lastProviderError ? (
          <Alert variant="destructive" data-testid="transaction-failure">
            <AlertTitle>{t(key("failure_title"))}</AlertTitle>
            <AlertDescription className="font-mono text-xs break-all">
              {provider?.lastProviderError ?? transaction.failureReason}
            </AlertDescription>
          </Alert>
        ) : null}

        <div className="grid gap-4 md:grid-cols-2">
          <Card density="compact">
            <CardHeader>
              <CardTitle className="text-sm">{t(key("attempt"))}</CardTitle>
            </CardHeader>
            <CardContent>
              <dl>
                <Fact
                  label={t(key("status"))}
                  value={
                    <Badge variant={attemptStatusVariant(transaction.status)}>
                      {transaction.statusLabel}
                    </Badge>
                  }
                />
                <Fact label={t(key("payment_method"))} value={transaction.paymentMethodLabel} />
                <Fact label={t(key("kind"))} value={t(key(`kind_${transaction.kind}`))} />
                <Fact label={t(key("subtotal"))} value={`$${transaction.selectedSubtotal}`} />
                <Fact label={t(key("tip"))} value={`$${transaction.tipAmount}`} />
                <Fact label={t(key("total"))} value={`$${transaction.totalAmount}`} />
                <Fact
                  label={t(key("service_fee"))}
                  value={`$${transaction.serviceFeeAmount} (${transaction.serviceFeePercentage}%)`}
                />
                <Fact label={t(key("created_at"))} value={formatDateTime(transaction.createdAt)} />
                <Fact
                  label={t(key("completed_at"))}
                  value={transaction.completedAt ? formatDateTime(transaction.completedAt) : null}
                />
                <Fact label={t(key("idempotency_key"))} value={transaction.idempotencyKey} mono />
              </dl>
            </CardContent>
          </Card>

          <Card density="compact">
            <CardHeader>
              <CardTitle className="text-sm">{t(key("context"))}</CardTitle>
            </CardHeader>
            <CardContent>
              <dl>
                <Fact label={t(key("location"))} value={transaction.location.name} />
                <Fact label={t(key("diner"))} value={transaction.diner.name} />
                <Fact label={t(key("diner_email"))} value={transaction.diner.email} />
                <Fact
                  label={t(key("bill"))}
                  value={
                    transaction.bill
                      ? `#${transaction.bill.id} · ${transaction.bill.statusLabel}`
                      : null
                  }
                />
                <Fact label={t(key("bill_split"))} value={transaction.billSplitId} />
                <Fact
                  label={t(key("selected_orders"))}
                  value={
                    transaction.selectedOrderIds.length > 0
                      ? transaction.selectedOrderIds.join(", ")
                      : t(key("no_orders"))
                  }
                />
              </dl>
            </CardContent>
          </Card>
        </div>

        <Card density="compact">
          <CardHeader>
            <CardTitle className="text-sm">{t(key("provider"))}</CardTitle>
          </CardHeader>
          <CardContent>
            {/* Not an error state and not an empty one: a cash attempt is
                supposed to look like this. What it rules out is Stripe. */}
            {provider ? (
              <dl className="grid gap-x-8 md:grid-cols-2">
                <Fact label={t(key("provider_name"))} value={provider.provider} />
                <Fact
                  label={t(key("provider_status"))}
                  value={<Badge variant="secondary">{provider.statusLabel}</Badge>}
                />
                <Fact label={t(key("provider_raw_status"))} value={provider.providerStatus} />
                <Fact
                  label={t(key("amount_charged"))}
                  value={money(provider.amountTotalCents, provider.currency)}
                />
                <Fact
                  label={t(key("application_fee"))}
                  value={money(provider.applicationFeeAmountCents, provider.currency)}
                />
                <Fact label={t(key("connected_account"))} value={provider.connectedAccountId} mono />
                <Fact label={t(key("checkout_session"))} value={provider.checkoutSessionId} mono />
                <Fact label={t(key("payment_intent"))} value={provider.paymentIntentId} mono />
                <Fact label={t(key("charge"))} value={provider.chargeId} mono />
                <Fact label={t(key("refund"))} value={provider.refundId} mono />
                <Fact
                  label={t(key("checkout_expires_at"))}
                  value={
                    provider.checkoutExpiresAt ? formatDateTime(provider.checkoutExpiresAt) : null
                  }
                />
                <Fact
                  label={t(key("provider_completed_at"))}
                  value={provider.completedAt ? formatDateTime(provider.completedAt) : null}
                />
              </dl>
            ) : (
              <p className="text-sm text-muted-foreground" data-testid="transaction-no-provider">
                {t(key("no_provider"))}
              </p>
            )}
          </CardContent>
        </Card>

        {/* The durable copy of what the diner agreed to. For a pickup checkout it
            is the only record of the cart until the webhook materialises it,
            which makes it the thing to read when a charge and an order
            disagree. */}
        <Card density="compact">
          <CardHeader>
            <CardTitle className="text-sm">{t(key("snapshot"))}</CardTitle>
          </CardHeader>
          <CardContent>
            <pre
              className="overflow-x-auto rounded-md border border-border bg-muted/40 p-3 font-mono text-xs text-foreground"
              data-testid="transaction-snapshot"
            >
              {JSON.stringify(transaction.snapshot, null, 2)}
            </pre>
          </CardContent>
        </Card>
      </MainContainer>
    </AdminLayout>
  )
}
